import React, { useState, useEffect, useCallback } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSearchbar,
  IonIcon,
  IonButton,
  IonButtons,
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonChip,
  IonBadge
} from '@ionic/react';
import {
  checkmarkCircle,
  checkmarkCircleOutline,
  bookmark,
  bookmarkOutline,
  chevronDown,
  chevronUp,
  listOutline
} from 'ionicons/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';

import { configService } from '../services/ConfigService';
import { storageService } from '../services/StorageService';
import MascotMessage from '../components/MascotMessage';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';

import './InterviewQuestions.css';

function InterviewQuestions() {
  const config = configService.getConfig();
  const interviews = configService.getInterviews();
  const meta = seoService.getPageMeta('interviews');
  
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [savedIds, setSavedIds] = useState([]);
  const [learnedIds, setLearnedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      const saved = storageService.get('interview_saved') || [];
      const learned = storageService.get('interview_learned') || [];
      setSavedIds(saved);
      setLearnedIds(learned);
      setLoading(false);
    };
    loadData();
  }, []);

  // Get questions from interviews data
  const questions = interviews?.questions || [];

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = searchText === '' || 
      q.question.toLowerCase().includes(searchText.toLowerCase()) ||
      q.answer?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesDifficulty = selectedDifficulty === 'all' || 
      q.difficulty === selectedDifficulty;
    
    let matchesView = true;
    if (viewMode === 'saved') {
      matchesView = savedIds.includes(q.id);
    } else if (viewMode === 'learned') {
      matchesView = learnedIds.includes(q.id);
    }
    
    return matchesSearch && matchesDifficulty && matchesView;
  });

  const toggleSaved = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setSavedIds(prev => {
      const updated = prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id];
      storageService.set('interview_saved', updated);
      return updated;
    });
  }, []);

  const markLearned = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setLearnedIds(prev => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      storageService.set('interview_learned', updated);
      return updated;
    });
  }, []);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds(prev => prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]);
  }, []);

  const getLanguageSyntax = () => {
    // Use configService.getLanguage() for correct language
    const language = configService.getLanguage();
    switch(language) {
      case 'python': return 'python';
      case 'java': return 'java';
      case 'javascript': return 'javascript';
      case 'cpp': case 'c': return 'cpp';
      default: return 'python';
    }
  };

  const totalCount = questions.length;
  const learnedCount = learnedIds.filter(id => questions.some(q => q.id === id)).length;
  const savedCount = savedIds.filter(id => questions.some(q => q.id === id)).length;
  const progressPercent = totalCount > 0 ? Math.round((learnedCount / totalCount) * 100) : 0;

  const getMascotMessage = () => {
    if (learnedCount === 0) {
      return "Ready to ace your interviews? Let's practice! 💼";
    } else if (learnedCount >= totalCount) {
      return "You've learned all questions! You're interview ready! 🎉";
    } else if (learnedCount >= totalCount / 2) {
      return "Great progress! Keep going! 🚀";
    } else {
      return `${learnedCount} questions learned. You got this! 💪`;
    }
  };

  const getMascotState = () => {
    if (learnedCount === 0) return 'explain';
    if (learnedCount >= totalCount) return 'celebrate';
    if (learnedCount >= totalCount / 2) return 'happy';
    return 'thinking';
  };

  if (loading) {
    return (
      <>
        <SEO
          title={meta.title}
          description={meta.description}
          keywords={meta.keywords}
          canonicalUrl={meta.canonicalUrl}
          ogImage={meta.ogImage}
        />
        <IonPage>
          <IonHeader>
          <IonToolbar color="primary">
            <IonButtons slot="start">
              <IonBackButton defaultHref="/home" />
            </IonButtons>
            <IonTitle>Interview Prep</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="interview-content">
          <div className="interview-loading">
            <div className="loading-spinner"></div>
            <p>Loading questions...</p>
          </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
      />
      <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Interview Prep</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="interview-content">
        <div className="interview-container">
          {/* Mascot Section */}
          <MascotMessage
            message={getMascotMessage()}
            state={getMascotState()}
            size="medium"
          />

          {/* Stats Cards */}
          <div className="stats-section">
            <IonCard className="stat-card">
              <IonCardContent>
                <div className="stat-icon">📚</div>
                <div className="stat-value">{totalCount}</div>
                <div className="stat-label">Total</div>
              </IonCardContent>
            </IonCard>
            <IonCard className="stat-card learned">
              <IonCardContent>
                <div className="stat-icon">✅</div>
                <div className="stat-value">{learnedCount}</div>
                <div className="stat-label">Learned</div>
              </IonCardContent>
            </IonCard>
            <IonCard className="stat-card saved">
              <IonCardContent>
                <div className="stat-icon"><IonIcon icon={bookmark} /></div>
                <div className="stat-value">{savedCount}</div>
                <div className="stat-label">Saved</div>
              </IonCardContent>
            </IonCard>
            <IonCard className="stat-card progress">
              <IonCardContent>
                <div className="stat-icon">📈</div>
                <div className="stat-value">{progressPercent}%</div>
                <div className="stat-label">Progress</div>
              </IonCardContent>
            </IonCard>
          </div>

          {/* View Mode Segment - 3 tabs */}
          <div className="segment-section">
            <div className="custom-segment">
              <button 
                className={`segment-btn ${viewMode === 'all' ? 'active' : ''}`}
                onClick={() => setViewMode('all')}
              >
                <IonIcon icon={listOutline} className="segment-icon" /> All
              </button>
              <button 
                className={`segment-btn ${viewMode === 'saved' ? 'active' : ''}`}
                onClick={() => setViewMode('saved')}
              >
                <IonIcon icon={bookmark} className="segment-icon" /> Saved
              </button>
              <button 
                className={`segment-btn ${viewMode === 'learned' ? 'active' : ''}`}
                onClick={() => setViewMode('learned')}
              >
                <IonIcon icon={checkmarkCircle} className="segment-icon" /> Learned
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="search-section">
            <IonSearchbar
              value={searchText}
              onIonInput={e => setSearchText(e.detail.value || '')}
              placeholder="Search questions..."
              debounce={300}
            />
          </div>

          {/* Difficulty Filter */}
          <div className="filter-section">
            <IonChip 
              className={`chip-all ${selectedDifficulty === 'all' ? 'chip-selected' : ''}`}
              onClick={() => setSelectedDifficulty('all')}
            >
              All
            </IonChip>
            <IonChip 
              className={`chip-easy ${selectedDifficulty === 'easy' ? 'chip-selected' : ''}`}
              onClick={() => setSelectedDifficulty('easy')}
            >
              🟢 Easy
            </IonChip>
            <IonChip 
              className={`chip-medium ${selectedDifficulty === 'medium' ? 'chip-selected' : ''}`}
              onClick={() => setSelectedDifficulty('medium')}
            >
              🟡 Medium
            </IonChip>
            <IonChip 
              className={`chip-hard ${selectedDifficulty === 'hard' ? 'chip-selected' : ''}`}
              onClick={() => setSelectedDifficulty('hard')}
            >
              🔴 Hard
            </IonChip>
          </div>

          {/* Results Count */}
          <div className="results-info">
            Showing {filteredQuestions.length} of {totalCount} questions
          </div>

          {/* Questions List */}
          {filteredQuestions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                {viewMode === 'saved' ? <IonIcon icon={bookmark} /> : viewMode === 'learned' ? <IonIcon icon={checkmarkCircle} /> : '🔍'}
              </div>
              <h3>
                {viewMode === 'saved' 
                  ? 'No saved questions yet' 
                  : viewMode === 'learned'
                  ? 'No learned questions yet'
                  : 'No questions found'}
              </h3>
              <p>
                {viewMode === 'saved' 
                  ? 'Tap the star icon to save questions for later' 
                  : viewMode === 'learned'
                  ? 'Mark questions as learned to track your progress'
                  : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <div className="questions-list">
              {filteredQuestions.map((q, index) => {
                const isExpanded = expandedIds.includes(q.id);
                const isLearned = learnedIds.includes(q.id);
                const isSaved = savedIds.includes(q.id);
                
                return (
                  <IonCard 
                    key={q.id}
                    className={`question-card ${isExpanded ? 'expanded' : ''} ${isLearned ? 'learned' : ''}`}
                  >
                    <IonCardContent className="question-header" onClick={() => toggleExpanded(q.id)}>
                      <div className="question-number-badge">
                        {isLearned ? <IonIcon icon={checkmarkCircle} /> : index + 1}
                      </div>
                      
                      <div className="question-main">
                        <div className="question-meta">
                          <IonBadge color={
                            q.difficulty === 'easy' ? 'success' : 
                            q.difficulty === 'medium' ? 'warning' : 'danger'
                          }>
                            {q.difficulty}
                          </IonBadge>
                          {isSaved && (
                            <IonBadge color="warning" className="saved-icon-badge">
                              <IonIcon icon={bookmark} />
                            </IonBadge>
                          )}
                          {isLearned && (
                            <IonBadge color="success" className="learned-icon-badge">
                              <IonIcon icon={checkmarkCircle} />
                            </IonBadge>
                          )}
                        </div>
                        <h3 className="question-text">{q.question}</h3>
                      </div>
                      
                      <div className="question-actions">
                        <IonButton 
                          fill="clear" 
                          size="small"
                          onClick={(e) => toggleSaved(q.id, e)}
                          color="warning"
                        >
                          <IonIcon 
                            key={`bookmark-header-${q.id}-${isSaved}`}
                            icon={isSaved ? bookmark : bookmarkOutline} 
                          />
                        </IonButton>
                        <IonIcon 
                          icon={isExpanded ? chevronUp : chevronDown} 
                          className="expand-icon"
                        />
                      </div>
                    </IonCardContent>
                    
                    {isExpanded && (
                      <div className="answer-section">
                        <div className="answer-block">
                          <h4>💡 Answer</h4>
                          <div className="answer-text markdown-content">
                            <ReactMarkdown>{q.answer}</ReactMarkdown>
                          </div>
                        </div>
                        
                        {q.codeExample && (
                          <div className="code-block">
                            <h5>💻 Code Example</h5>
                            <SyntaxHighlighter
                              language={getLanguageSyntax()}
                              style={vscDarkPlus}
                              customStyle={{ borderRadius: '12px', fontSize: '13px' }}
                              showLineNumbers
                            >
                              {q.codeExample}
                            </SyntaxHighlighter>
                          </div>
                        )}
                        
                        {q.keyPoints && q.keyPoints.length > 0 && (
                          <div className="key-points-block">
                            <h5>✨ Key Points</h5>
                            <ul>
                              {q.keyPoints.map((point, i) => (
                                <li key={i}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="answer-actions">
                          <IonButton
                            fill={isSaved ? 'solid' : 'outline'}
                            color="warning"
                            size="small"
                            onClick={(e) => toggleSaved(q.id, e)}
                          >
                            <IonIcon 
                              key={`bookmark-action-${q.id}-${isSaved}`}
                              icon={isSaved ? bookmark : bookmarkOutline} 
                              slot="start" 
                            />
                            {isSaved ? 'Saved' : 'Save'}
                          </IonButton>
                          
                          {!isLearned ? (
                            <IonButton
                              fill="solid"
                              color="success"
                              size="small"
                              onClick={(e) => markLearned(q.id, e)}
                            >
                              <IonIcon icon={checkmarkCircleOutline} slot="start" />
                              Mark as Learned
                            </IonButton>
                          ) : (
                            <div className="learned-badge">
                              <IonIcon icon={checkmarkCircle} />
                              Learned
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </IonCard>
                );
              })}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default InterviewQuestions;